import { motion } from "framer-motion";
import { Sparkles, Leaf, Gem, Sun, Atom, Droplet, ShieldCheck } from "lucide-react";
import { SectionLabel } from "@/components/Reveal";
import { ASSETS } from "@/lib/site";

const tile = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] } },
};

function Tile({ className = "", children, testid }) {
  return (
    <motion.div
      variants={tile}
      data-testid={testid}
      className={`bg-base border border-champagne rounded-sm p-8 shadow-sm ${className}`}
    >
      {children}
    </motion.div>
  );
}

const ACTIVES = [
  ["L-Glutathione", "OPITAC™ Reduced · Japan", Atom],
  ["Vitamin C", "Brightening synergy", Sun],
  ["Vitamin D3", "Skin health", Droplet],
  ["Zinc Sulphate", "Clarity & repair", ShieldCheck],
  ["Curcumin", "Anti-inflammatory", Leaf],
];

export default function Ingredients() {
  return (
    <section
      id="formula"
      data-testid="formula-section"
      className="relative z-10 py-24 md:py-32"
    >
      <div className="mx-auto max-w-[1400px] px-5 md:px-10">
        <div className="max-w-3xl mb-12">
          <SectionLabel index="04">The Formula</SectionLabel>
          <h2 className="font-serif font-light tracking-tight leading-[1.03] text-4xl md:text-6xl text-[color:var(--relumia-ink)]">
            One tablet. <span className="italic text-gold">Five</span> reasons to glow.
          </h2>
        </div>

        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-60px" }}
          transition={{ staggerChildren: 0.08 }}
          className="grid grid-cols-2 lg:grid-cols-4 gap-5 md:gap-8"
        >
          {/* Product spotlight tile */}
          <Tile
            testid="formula-product-tile"
            className="col-span-2 row-span-2 !p-0 overflow-hidden bg-surface relative min-h-[320px]"
          >
            <img
              src={ASSETS.productHero}
              alt="Relumia pack"
              className="absolute inset-0 h-full w-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/55 to-transparent" />
            <div className="absolute bottom-0 p-8 text-white">
              <div className="overline !text-white/80 mb-2">30 Tablets · 3×10 Alu-Alu</div>
              <div className="font-serif text-3xl font-light">Relumia<sup className="text-xs">TM</sup></div>
              <div className="text-sm text-white/80">Skin Lightening Tablet</div>
            </div>
          </Tile>

          <Tile testid="benefit-brightening" className="col-span-2">
            <Sparkles className="text-gold mb-4" size={26} strokeWidth={1.4} />
            <div className="font-serif text-2xl text-[color:var(--relumia-ink)]">Brightening Support</div>
            <p className="text-[14px] leading-relaxed text-[color:var(--relumia-muted)] mt-2">
              Helps reduce dark spots and even out uneven skin tone.
            </p>
          </Tile>

          <Tile testid="benefit-antioxidant">
            <Leaf className="text-gold mb-4" size={26} strokeWidth={1.4} />
            <div className="font-serif text-xl text-[color:var(--relumia-ink)]">Powerful Antioxidants</div>
            <p className="text-[13px] leading-relaxed text-[color:var(--relumia-muted)] mt-2">
              Shields skin from free-radical and environmental stress.
            </p>
          </Tile>

          <Tile testid="benefit-radiant">
            <Gem className="text-gold mb-4" size={26} strokeWidth={1.4} />
            <div className="font-serif text-xl text-[color:var(--relumia-ink)]">Youthful Radiance</div>
            <p className="text-[13px] leading-relaxed text-[color:var(--relumia-muted)] mt-2">
              Promotes healthy, glowing and youthful-looking skin.
            </p>
          </Tile>
        </motion.div>

        {/* Actives row */}
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-60px" }}
          transition={{ staggerChildren: 0.06 }}
          className="grid grid-cols-2 md:grid-cols-5 gap-5 md:gap-8 mt-5 md:mt-8"
        >
          {ACTIVES.map(([t, s, Icon]) => (
            <Tile key={t} testid={`active-${t.toLowerCase().replace(/\s+/g, "-")}`}>
              <Icon className="text-gold mb-4" size={24} strokeWidth={1.4} />
              <div className="font-serif text-lg text-[color:var(--relumia-ink)] leading-tight">{t}</div>
              <div className="text-[12px] tracking-wide text-[color:var(--relumia-muted)] mt-1">{s}</div>
            </Tile>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
